import { Base, variables } from '@bitrise/bitkit';
import {
  SeriesPointXY,
  Viz,
  VizHTML,
  VizHTMLArtboard,
  VizSVG,
  VizSVGAxisX,
  VizSVGAxisY,
  VizSVGGridLinesAxisY,
} from '@bitrise/bitviz';
import { DataTimeSeries, OverviewMetric } from '@trace/types';
import zip from 'lodash.zip';
import React, { FunctionComponent, PointerEvent, useCallback, useMemo, useState } from 'react';
import getFormattedValue from '../../../utils/metric/getFormattedValue';
import MessageFailing from '../../ViewVisualisations/MessageFailing';
import MessageFetching from '../../ViewVisualisations/MessageFetching';
import OverviewCard from '../OverviewCard/OverviewCard';
import OverviewTitle from '../OverviewCard/OverviewTitle';
import OverviewComparison from '../OverviewComparison/OverviewComparison';
import MinAvgMaxDataPoint, { MinMaxPoint } from './MinAvgMaxDataPoint';
import OverviewSessionDurationTooltip from './OverviewSessionDurationTooltip';

interface Props {
  dataAvg?: OverviewMetric;
  dataMax?: OverviewMetric;
  dataMin?: OverviewMetric;
  isError: boolean;
  isFetching: boolean;
  refetch: () => void;
  xAxisLabels: [string, number][];
}

type ChartData = [MinMaxPoint[], number, number];

const getChartData = (
  dataAvg: OverviewMetric = { app: [], appVersions: {} },
  dataMin: OverviewMetric = { app: [], appVersions: {} },
  dataMax: OverviewMetric = { app: [], appVersions: {} },
): ChartData => {
  let yMin: number | null = null;
  let yMax: number | null = null;

  const setYMinMax = (y?: null | number) => {
    if (y !== null && y !== undefined && (yMin === null || y < yMin)) yMin = y;
    if (y !== null && y !== undefined && (yMax === null || y > yMax)) yMax = y;
  };

  const getSeries = (
    avgSeries: DataTimeSeries = [],
    minSeries: DataTimeSeries = [],
    maxSeries: DataTimeSeries = [],
  ) =>
    zip(avgSeries, minSeries, maxSeries).map(([avg = null, min = null, max = null], x) => {
      setYMinMax(avg);
      setYMinMax(min);
      setYMinMax(max);
      return { x, avg, min, max };
    });

  const seriesApp = getSeries(dataAvg.app, dataMin.app, dataMax.app);

  const yLower = yMin || 0;
  const yUpper = yMin === yMax || yMax === null ? yLower + 1 : yMax;

  return [seriesApp, yLower, yUpper];
};

const OverviewSessionDuration: FunctionComponent<Props> = ({
  dataAvg,
  dataMax,
  dataMin,
  isError,
  isFetching,
  refetch,
  xAxisLabels,
}) => {
  const [focusPoint, setFocusPoint] = useState<SeriesPointXY | null>(null);
  const [seriesApp, , yMax] = useMemo(() => getChartData(dataAvg, dataMin, dataMax), [
    dataAvg,
    dataMin,
    dataMax,
  ]);

  const handleOnPointerMove = useCallback(
    (_: PointerEvent, point: SeriesPointXY) => {
      if (point.x !== null && seriesApp[point.x]) {
        setFocusPoint({
          x: point.x,
          y: seriesApp[point.x].max,
        });
      }
    },
    [seriesApp],
  );

  return (
    <OverviewCard>
      <OverviewTitle title="Session Duration">
        <OverviewComparison align="end" data={dataAvg} isFetching={isFetching} unit="seconds" />
      </OverviewTitle>

      <Viz
        height={200}
        paddingBottom={22}
        paddingLeft={34}
        paddingTop={22}
        onPointerLeave={() => setFocusPoint(null)}
        onPointerMove={handleOnPointerMove}
        xDomain={xAxisLabels.map((_, x) => x)}
        xDomainScale="band"
        xDomainPaddingOuter={0.5}
        yDomain={[0, yMax]}
      >
        <VizHTML>
          <VizHTMLArtboard>
            <Base borderRadius="x2" backgroundColor="gray-1" width="100%" height="100%" />
          </VizHTMLArtboard>
        </VizHTML>

        <VizSVG>
          <VizSVGAxisX
            fill={variables.colorGray6}
            ticksOnly
            tickLineLength={0.001}
            format={(x) => xAxisLabels[x][0]}
          />

          <VizSVGAxisY
            fill={variables.colorGray6}
            flip
            format={(v) => getFormattedValue('seconds', v)}
            tickLineLength={0.001}
            tickCount={3}
            ticksOnly
          />

          {!isError && !isFetching && (
            <VizSVGGridLinesAxisY
              animate
              stroke={variables.colorGray5}
              strokeDasharray="2 6"
              style={{ pointerEvents: 'none' }}
              tickCount={3}
            />
          )}

          {seriesApp.map((point, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <MinAvgMaxDataPoint {...point} key={index} />
          ))}
        </VizSVG>

        <VizHTML>
          <VizHTMLArtboard>
            <MessageFetching fetching={isFetching} />
            <MessageFailing failing={isError} onFetch={refetch} />
          </VizHTMLArtboard>
        </VizHTML>

        <OverviewSessionDurationTooltip
          {...(focusPoint && focusPoint.x !== null && seriesApp[focusPoint.x])}
          point={focusPoint}
          visible={!!focusPoint}
        />
      </Viz>
    </OverviewCard>
  );
};

export default OverviewSessionDuration;
